import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import {
  Users,
  Loader2,
  Search,
  MessageCircle,
  ChevronDown,
  Sparkles,
  Heart,
  Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

const SocialPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'mando' ? 'mando' : 'user');
  const [likedIds, setLikedIds] = useState(new Set());
  const [leaderboard, setLeaderboard] = useState(null);
  const limit = 12;

  const fetchPosts = useCallback(async (reset = false) => {
    try {
      const currentOffset = reset ? 0 : offset;
      let url = `/community?limit=${limit}&offset=${currentOffset}&source=${tab === 'mando' ? 'mandomaster' : 'user'}`;

      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await api.get(url);

      if (reset) {
        setPosts(response.data);
        setOffset(limit);
      } else {
        setPosts(prev => [...prev, ...response.data]);
        setOffset(currentOffset + limit);
      }

      setHasMore(response.data.length === limit);
    } catch (error) {
      console.error('Failed to fetch community posts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [offset, search, tab]);

  useEffect(() => {
    setLoading(true);
    setOffset(0);
    fetchPosts(true);
  }, [search, tab]);

  useEffect(() => {
    api.get('/community/likes/mine').then((r) => setLikedIds(new Set(r.data.cosplay_ids))).catch(() => {});
    api.get('/community/likes/leaderboard').then((r) => setLeaderboard(r.data)).catch(() => {});
  }, []);

  const toggleLike = async (e, postId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await api.post(`/community/${postId}/like`);
      setLikedIds((prev) => {
        const n = new Set(prev);
        if (res.data.liked) n.add(postId); else n.delete(postId);
        return n;
      });
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, like_count: res.data.like_count } : p)));
    } catch (err) {
      console.error('Failed to toggle like', err);
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      fetchPosts(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-slate-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="social-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-black flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-black" />
            Community
          </h1>
          <p className="text-slate-700 mt-1">Discover amazing cosplays from the community</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="user">User Projects</TabsTrigger>
          <TabsTrigger value="mando">Mando</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-3 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600 z-10" />
          <Input
            type="text"
            placeholder="Search cosplays, characters, series..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Button
          type="submit"
          className="win-btn-default"
          data-testid="search-btn"
        >
          Search
        </Button>
      </form>

      {leaderboard && leaderboard.top_builds && leaderboard.top_builds.length > 0 && (
        <fieldset className="win-groupbox">
          <legend className="win-groupbox-label flex items-center gap-1">
            <Trophy className="w-4 h-4" /> Top Builds
          </legend>
          <div className="flex flex-wrap gap-2">
            {leaderboard.top_builds.map((b, i) => (
              <Link key={b.id} to={`/social/${b.id}`} className="win-well flex items-center gap-2 px-3 py-1.5">
                <span className="font-bold text-sm">#{i + 1}</span>
                <span className="text-sm text-black truncate max-w-[160px]">{b.title}</span>
                <span className="flex items-center gap-1 text-sm">♥ {b.likes}</span>
              </Link>
            ))}
          </div>
        </fieldset>
      )}

      {/* Posts Grid */}
      {posts.length === 0 ? (
        <div className="win-well p-12 text-center">
          <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-heading font-semibold text-black mb-2">No Shared Cosplays Yet</h3>
          <p className="text-slate-700 mb-6">
            {search
              ? `No cosplays found matching "${search}"`
              : 'Be the first to share your cosplay with the community!'}
          </p>
          <Link to="/cosplays">
            <Button className="win-btn-default gap-2">
              <Sparkles className="w-5 h-5" />
              View My Cosplays
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/social/${post.id}`}
                className="group"
                data-testid={`community-post-${post.id}`}
              >
                <div className="glass-card overflow-hidden h-full flex flex-col">
                  {/* Cover Image */}
                  <div className="aspect-[4/5] relative overflow-hidden bg-[var(--win-face)]">
                    {post.cover_image ? (
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[var(--win-face)]">
                        <Users className="w-16 h-16 text-slate-500" />
                      </div>
                    )}

                    {post.source === 'mandomaster' && (
                      <div className="absolute top-3 left-3 win-raised px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                        Mando
                      </div>
                    )}

                    {/* Comment count */}
                    {post.comment_count > 0 && (
                      <div className="absolute top-3 right-3 win-raised px-2 py-0.5 flex items-center gap-1">
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span className="text-xs font-medium">{post.comment_count}</span>
                      </div>
                    )}

                    {/* Like button */}
                    <button
                      onClick={(e) => toggleLike(e, post.id)}
                      aria-pressed={likedIds.has(post.id)}
                      aria-label={likedIds.has(post.id) ? 'Unlike' : 'Like'}
                      className={`absolute bottom-3 right-3 px-2 py-0.5 flex items-center gap-1 ${likedIds.has(post.id) ? 'win-selected' : ''}`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${likedIds.has(post.id) ? 'fill-current' : ''}`} />
                      <span className="text-xs font-medium">{post.like_count || 0}</span>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-heading font-semibold text-black text-lg truncate">
                      {post.title}
                    </h3>
                    <p className="text-slate-700 text-sm truncate">
                      {post.character_name}
                      {post.series_name && ` • ${post.series_name}`}
                    </p>

                    {/* User info */}
                    <div className="flex items-center gap-2 mt-auto pt-3 border-t border-[var(--win-shadow)]">
                      {post.user_profile_picture ? (
                        <img
                          src={post.user_profile_picture}
                          alt={post.user_name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-[var(--win-titlebar-start)] flex items-center justify-center">
                          <span className="text-xs text-white font-bold">
                            {post.user_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <span className="text-sm text-slate-800 truncate flex-1">{post.user_name}</span>
                      <span className="text-xs text-slate-600">{formatDate(post.shared_at)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outline"
                className="gap-2"
                data-testid="load-more-btn"
              >
                {loadingMore ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ChevronDown className="w-5 h-5" />
                    Load More
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SocialPage;
